/**
 * Verdict → presentation mappings (Tailwind token classes only, no hex).
 *
 * The matrix needs, per clinical-severity tone:
 *   - a solid cell fill        (bg-tone-*)
 *   - a soft surface wash      (bg-tone-*-tint) for verdict cards / stat tiles
 *   - a text colour            (text-tone-*) for the verdict headline + dot
 *
 * `no_data` (tone `muted`) deliberately fills with a neutral, not the muted
 * tone solid — a grey "no information" cell must read as absence, not as a
 * fifth severity. Mirrors the design handoff (cell.muted → neutral-200).
 */

import type { VerdictId, VerdictTone } from './types'
import { VERDICT_TONE } from './types'

/** Solid cell fill for the grid. */
export const TONE_FILL: Record<VerdictTone, string> = {
  ok: 'bg-tone-ok',
  warn: 'bg-tone-warn',
  bad: 'bg-tone-bad',
  // Absence reads as neutral, not as a tone (see module note).
  muted: 'bg-neutral-200',
}

/** Soft surface wash behind verdict cards / accepted rows / stat tiles. */
export const TONE_TINT: Record<VerdictTone, string> = {
  ok: 'bg-tone-ok-tint',
  warn: 'bg-tone-warn-tint',
  bad: 'bg-tone-bad-tint',
  muted: 'bg-tone-mute-tint',
}

/** Text colour for the verdict headline + the tone-dot. */
export const TONE_TEXT: Record<VerdictTone, string> = {
  ok: 'text-tone-ok',
  warn: 'text-tone-warn',
  bad: 'text-tone-bad',
  muted: 'text-tone-mute',
}

/** Legend swatch fill — same fills the matrix uses (muted shown as neutral). */
export const LEGEND_SWATCH: Record<VerdictTone, string> = TONE_FILL

/** Convenience: the tone for a verdict (re-export of the contract map). */
export function toneOf(verdict: VerdictId): VerdictTone {
  return VERDICT_TONE[verdict]
}
